import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResearchShell from '../components/research/ResearchShell'
import { SectorChips, StatTile } from '../components/research/charts'
import type { ConvertibleIssue, ConvertibleScanResult, ConvertVerdict, Sector } from '../types/research'
import { scanConvertibles, isResearchUsingMockData } from '../lib/research-api'

type KindFilter = 'all' | 'note' | 'preferred'

function card(extra = '') {
  return `bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 ${extra}`
}

function VerdictBadge({ v }: { v: ConvertVerdict }) {
  const cls =
    v === 'undervalued'
      ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
      : v === 'rich'
        ? 'bg-rose-500/15 text-rose-600 dark:text-rose-300'
        : 'bg-slate-500/15 text-slate-600 dark:text-slate-300'
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{v}</span>
}

function Term({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-900 dark:text-white">{value}</p>
    </div>
  )
}

function IssueDetail({ issue }: { issue: ConvertibleIssue }) {
  const navigate = useNavigate()
  const optionMarket = issue.embeddedOptionPct
  const optionModel = +(issue.modelValuePct - issue.bondFloorPct).toFixed(2)
  return (
    <div className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-4 space-y-4">
      {/* Redemption metrics */}
      <div>
        <h4 className="text-xs font-semibold text-slate-900 dark:text-white mb-2">Redemption metrics</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Term label="First call date" value={issue.callDate} />
          <Term label="Call / redemption price" value={`${issue.callPricePct}% of par`} />
          <Term
            label="Soft-call trigger"
            value={issue.softCallTriggerPct ? `${issue.softCallTriggerPct}% of conv. price` : 'None (hard call)'}
            hint="Stock level at which the issuer may force conversion"
          />
          <Term label={issue.maturity ? 'Maturity' : 'Structure'} value={issue.maturity ?? 'Perpetual'} />
        </div>
      </div>

      {/* Parity math */}
      <div>
        <h4 className="text-xs font-semibold text-slate-900 dark:text-white mb-2">Options-chain parity check</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Term
            label="Conversion option"
            value={`${issue.conversionRatio.toFixed(4)} sh @ $${issue.conversionPrice}`}
            hint="Shares per unit of par, struck at the conversion price"
          />
          <Term label="Parity" value={`${issue.parityPct.toFixed(1)}%`} hint="Conversion ratio × stock price, % of par" />
          <Term label="Bond floor" value={`${issue.bondFloorPct.toFixed(1)}%`} hint={`Straight value at rf + ${issue.creditSpreadBps}bp credit spread`} />
          <Term label="Tenor priced" value={`${issue.tenorYears.toFixed(1)} yr`} />
          <Term
            label="Embedded option (market)"
            value={`${optionMarket.toFixed(1)}% of par`}
            hint="Convert market price minus bond floor"
          />
          <Term
            label="Embedded option (model)"
            value={`${optionModel.toFixed(1)}% of par`}
            hint="Black–Scholes at the listed chain's tenor-matched IV"
          />
          <Term
            label="Convert-implied vol"
            value={issue.convertImpliedVol != null ? `${issue.convertImpliedVol}%` : 'n/a'}
            hint="Vol that reprices the embedded option to its market cost"
          />
          <Term label="Listed chain IV (matched)" value={`${issue.listedIv}%`} hint="Interpolated to the conversion tenor from the listed term structure" />
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-3">
          {issue.volGapPts != null && issue.volGapPts > 0
            ? `The embedded conversion option is priced at ${issue.convertImpliedVol}% vol vs ${issue.listedIv}% on the listed chain — buying the same optionality ${issue.volGapPts} vol points cheaper through the ${issue.kind.toLowerCase()}.`
            : issue.volGapPts != null && issue.volGapPts < 0
              ? `The embedded option costs ${Math.abs(issue.volGapPts)} vol points MORE than listed options — the convert is rich relative to chain parity.`
              : 'Market price sits near the bond floor; the embedded option carries little measurable vol.'}
        </p>
      </div>

      {/* Links */}
      <div className="flex flex-wrap gap-4 text-xs">
        {issue.sourceFiling && (
          <a
            href={issue.sourceFiling.edgarUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Source filing: {issue.sourceFiling.formType} ({issue.sourceFiling.filedAt}) ↗
          </a>
        )}
        <Link to={`/dashboard/financial-app/options/${issue.symbol}`} className="text-blue-600 dark:text-blue-400 hover:underline">
          Listed options chain
        </Link>
        <button
          onClick={() => navigate(`/dashboard/financial-app/research?task=analyze-filing&symbol=${issue.symbol}`)}
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          Ask Opus about the filing
        </button>
      </div>
    </div>
  )
}

export default function ConvertiblesPage() {
  const [sector, setSector] = useState<Sector | null>(null)
  const [kind, setKind] = useState<KindFilter>('all')
  const [result, setResult] = useState<ConvertibleScanResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [isMock, setIsMock] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        const r = await scanConvertibles({ sector, kind })
        if (cancelled) return
        setResult(r)
        setIsMock(isResearchUsingMockData())
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [sector, kind])

  const summary = useMemo(() => {
    const issues = result?.issues ?? []
    const undervalued = issues.filter((i) => i.verdict === 'undervalued')
    const gaps = issues.map((i) => i.volGapPts).filter((g): g is number => g != null)
    return {
      count: issues.length,
      undervalued: undervalued.length,
      avgGap: gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 0,
      bestCheapness: issues[0]?.cheapnessPct ?? 0,
    }
  }, [result])

  return (
    <ResearchShell showMockBanner={isMock && !loading}>
      {/* Controls */}
      <div className={card('mb-6')}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Convertible &amp; preferred parity scanner
          </h2>
          <label className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Instrument</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as KindFilter)}
              className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            >
              <option value="all">Notes &amp; preferred</option>
              <option value="note">Convertible notes</option>
              <option value="preferred">Convertible preferred</option>
            </select>
          </label>
        </div>
        <div className="mb-3">
          <SectorChips value={sector} onChange={setSector} />
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Reads conversion and redemption terms from corporate filings, strips each instrument to bond floor +
          embedded conversion option, then prices that option against the listed options chain. A positive vol gap
          means the convert sells the same optionality cheaper than the chain — potentially undervalued.
        </p>
      </div>

      {loading && (
        <div className={card('animate-pulse')}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-9 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          ))}
        </div>
      )}

      {!loading && result && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Instruments scanned" value={String(summary.count)} sub={sector ?? 'All sectors'} />
            <StatTile label="Undervalued vs parity" value={String(summary.undervalued)} tone={summary.undervalued > 0 ? 'good' : 'neutral'} sub="cheapness ≥ 2%" />
            <StatTile
              label="Avg vol gap"
              value={`${summary.avgGap >= 0 ? '+' : ''}${summary.avgGap.toFixed(1)} pts`}
              sub="listed IV − convert IV"
              tone={summary.avgGap > 0 ? 'good' : 'neutral'}
            />
            <StatTile label="Risk-free rate" value={`${result.riskFreeRatePct}%`} sub="pricing input" />
          </div>

          <div className={card()}>
            <p className="text-xs text-slate-500 mb-2">
              {result.issues.length} instruments · ranked by cheapness vs model · as of{' '}
              {new Date(result.asOf).toLocaleTimeString()}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-3 font-medium">Issuer</th>
                    <th className="py-2 pr-3 font-medium hidden lg:table-cell">Instrument</th>
                    <th className="py-2 pr-3 font-medium text-right" title="Coupon or preferred dividend rate">Cpn</th>
                    <th className="py-2 pr-3 font-medium text-right hidden sm:table-cell" title="Conversion price vs current stock price">Conv px / Stock</th>
                    <th className="py-2 pr-3 font-medium text-right hidden md:table-cell" title="Market price, % of par">Mkt px</th>
                    <th className="py-2 pr-3 font-medium text-right hidden md:table-cell" title="Conversion premium over parity">Premium</th>
                    <th className="py-2 pr-3 font-medium text-right" title="Listed chain IV minus convert-implied vol">Vol gap</th>
                    <th className="py-2 pr-3 font-medium text-right" title="(Model − market) / market">Cheapness</th>
                    <th className="py-2 font-medium text-right">Verdict</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
                  {result.issues.map((iss) => (
                    <Fragment key={iss.id}>
                      <tr
                        onClick={() => setExpanded(expanded === iss.id ? null : iss.id)}
                        className="hover:bg-slate-100/70 dark:hover:bg-slate-700/20 transition-colors cursor-pointer"
                        title="Click to expand parity math and redemption terms"
                      >
                        <td className="py-2.5 pr-3">
                          <span className="font-semibold text-slate-900 dark:text-white">{iss.symbol}</span>
                          <span className="block text-xs text-slate-500 max-w-[150px] truncate">{iss.company}</span>
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400 hidden lg:table-cell">{iss.kind}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300">{iss.couponPct}%</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden sm:table-cell">
                          ${iss.conversionPrice} / ${iss.stockPrice}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden md:table-cell">{iss.marketPricePct.toFixed(1)}%</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden md:table-cell">{iss.conversionPremiumPct}%</td>
                        <td className={`py-2.5 pr-3 text-right font-semibold ${
                          iss.volGapPts == null
                            ? 'text-slate-500'
                            : iss.volGapPts > 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-rose-600 dark:text-rose-400'
                        }`}>
                          {iss.volGapPts == null ? '—' : `${iss.volGapPts > 0 ? '+' : ''}${iss.volGapPts}`}
                        </td>
                        <td className={`py-2.5 pr-3 text-right font-semibold ${
                          iss.cheapnessPct > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                        }`}>
                          {iss.cheapnessPct > 0 ? '+' : ''}
                          {iss.cheapnessPct}%
                        </td>
                        <td className="py-2.5 text-right">
                          <VerdictBadge v={iss.verdict} />
                        </td>
                      </tr>
                      {expanded === iss.id && (
                        <tr>
                          <td colSpan={9} className="py-3">
                            <IssueDetail issue={iss} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              {result.issues.length === 0 && (
                <p className="text-center text-sm text-slate-500 py-8">
                  No convertible or preferred instruments found for this scope.
                </p>
              )}
            </div>
          </div>

          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            Pricing uses a bond-floor + Black–Scholes decomposition against tenor-matched listed IV. Research
            analytics for educational use — not investment advice.
          </p>
        </div>
      )}
    </ResearchShell>
  )
}
